import React, { useEffect, useRef } from "react";
import {
  LandingLogo,
  LandingScrollingContainer,
  MainLandingContainer,
  MainLandingImage,
  MainLandingImageContainer,
  LandingParagraph,
  Wrapper,
  LandingSubtitle,
  LandingSubtitleHeadingContainer,
  LandingSubtitleHeading,
  LandingSubtitleSceneContainer,
  LandingHeadingContainer,
  ScrollingContainer,
  LandingScrollingWrapper,
  LandingHeadingWrapper,
} from "../styles/components/landing";
import animations from "../animations/animations";
import { BREAKPOINTS } from "../styles/variables/breakpoints";
import {
  LandingVibeHeadingDesktop,
  LandingVibeHeadingMobile,
  LandingSubtitleMobile,
} from "../data/landingHeadings";
import { getDeviceInfo } from "../data/utils";

const LandingSubtitleComponent = ({ sceneRef, desktop = false }) => {
  return (
    <LandingSubtitle $desktop={desktop} $mobile={!desktop}>
      <LandingSubtitleHeadingContainer>
        {sceneRef ? (
          <>
            <LandingSubtitleHeading $subtitleCount={0}>
              Refresh
            </LandingSubtitleHeading>
            <LandingSubtitleHeading $subtitleCount={1}>
              &nbsp;Your
            </LandingSubtitleHeading>
            <LandingSubtitleSceneContainer ref={sceneRef} />
            <LandingSubtitleHeading $subtitleCount={2}>
              Rhythm
            </LandingSubtitleHeading>
          </>
        ) : (
          <LandingSubtitleMobile />
        )}
      </LandingSubtitleHeadingContainer>
    </LandingSubtitle>
  );
};

const LandingComponent = props => {
  const wrapperRef = useRef(null);
  const scrollingContainerRef = useRef(null);
  const paragraphRef = useRef(null);
  const mainImageContainerRef = useRef(null);
  const {
    handleSetZoomingAnimation,
    cameraRef,
    planesRef,
    parentContainerRef,
    sceneRef,
    ...rest
  } = props;

  const componentContent = Object.values(rest).map(component => ({
    key: component.key ? component.key : component.moduleTitle,
    type: component.__typename ? component.__typename : component.key,
    content: component.images ? component.images : component.text,
  }));

  const { isHSApp } = getDeviceInfo();

  useEffect(() => {
    if (
      !sceneRef.current ||
      !parentContainerRef ||
      typeof window === "undefined"
    )
      return;

    const mm = animations.matchMedia();

    const appWrapper = document.querySelector(".app-wrapper");
    const wrapper = document.querySelector("#lenis-root");
    const componentLanding = document.querySelector(".component--landing");
    const activePlane = planesRef.current[0];
    const animationHeadings = document.querySelectorAll(".label-inner");
    const bodegaContainer = document.querySelector(".bodega-container");
    const activeHeading = animationHeadings[0];
    const activeNavigation = document.querySelectorAll(
      'div[id^="navigation-"]'
    )[0];
    const mainImages = mainImageContainerRef.current.querySelectorAll("img");

    if (!cameraRef.current) return;

    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      animations
        .timeline({
          scrollTrigger: {
            trigger: componentLanding,
            start: "top top",
            end: "bottom bottom",
            scroller: wrapper,
            pin: true,
            pinSpacing: false,
            anticipatePin: 1,
            scrub: 1,
            invalidateOnRefresh: true,
            id: "landingScrollTrigger",
            onEnter: () => {
              console.log("🟢 onEnter - LandingComponent");
              appWrapper.style.pointerEvents = "none";
              handleSetZoomingAnimation(true);
            },
            onEnterBack: () => {
              console.log("🔁 onEnterBack - LandingComponent");
              appWrapper.style.pointerEvents = "none";
              handleSetZoomingAnimation(true);
            },
            onLeave: () => {
              console.log("🚪 onLeave - LandingComponent");
              appWrapper.style.pointerEvents = "auto";
              handleSetZoomingAnimation(false);
            },
            onLeaveBack: () => {
              console.log("⬅️ onLeaveBack - LandingComponent");
              appWrapper.style.pointerEvents = "auto";
              handleSetZoomingAnimation(false);
            },
          },
        })
        .to(scrollingContainerRef.current, { y: -350, duration: 4 }, 0)
        .to(
          mainImages,
          {
            filter: "brightness(0.75)",
            duration: 1,
            ease: "none",
          },
          0
        )
        .to(
          cameraRef.current.position,
          {
            delay: 0.8,
            duration: 1,
            z: 5,
            ease: "power2.out",
          },
          "animateLabel"
        )
        .to(
          activePlane.position,
          {
            delay: 0.8,
            x: activePlane.userData.position.x,
            y: activePlane.userData.position.y,
            duration: 1,
            ease: "power2.out",
          },
          "animateLabel"
        )
        .to(
          wrapperRef.current,
          {
            delay: 0.8,
            duration: 1.5,
            opacity: 0,
          },
          "animateLabel"
        )
        .addLabel("removeBlur", "<=0.5")
        .to(
          [activeHeading, activeNavigation],
          {
            filter: "blur(0px)",
            duration: 0.4,
          },
          "removeBlur"
        )
        .to(animationHeadings, { opacity: 1 }, "removeBlur")
        .addLabel("endAnimation", "<=0.5")
        .to(planesRef.current, { visible: true }, "endAnimation")
        .to(bodegaContainer, { opacity: 1 }, "endAnimation");
    });

    mm.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
      animations
        .timeline({
          scrollTrigger: {
            trigger: componentLanding,
            start: "top top",
            end: "+=100%",
            pin: true,
            pinSpacing: true,
            anticipatePin: 1,
            scrub: 0.2,
            id: "landingScrollTriggerMobile",
          },
        })
        .to(scrollingContainerRef.current, { y: -900, duration: 2 }, 0);
    });

    return () => mm.revert();
  }, [parentContainerRef]);

  return (
    <Wrapper ref={wrapperRef}>
      <MainLandingContainer>
        {componentContent
          .filter(component => component.key === "component--landing-image")
          .map((component, index) => (
            <MainLandingImageContainer
              ref={mainImageContainerRef}
              key={`${component.key}-${index}`}
            >
              <MainLandingImage
                src={component.content[0]?.image?.fluid?.src}
                $desktop
              />
              <MainLandingImage
                src={component.content[0]?.imageMobile?.fluid?.src}
                $mobile
              />
            </MainLandingImageContainer>
          ))}
        <LandingScrollingWrapper>
          <LandingScrollingContainer>
            <ScrollingContainer ref={scrollingContainerRef}>
              {componentContent.map((component, index) => {
                switch (component.key) {
                  case "component--landing-logo":
                    return (
                      <LandingHeadingWrapper key={`${component.key}-${index}`}>
                        <LandingLogo
                          $isHSApp={isHSApp}
                          src={component.content[0]?.image?.fluid?.src}
                        />
                        <LandingHeadingContainer $desktop>
                          <LandingVibeHeadingDesktop />
                        </LandingHeadingContainer>
                        <LandingHeadingContainer $mobile>
                          <LandingVibeHeadingMobile />
                        </LandingHeadingContainer>
                        <LandingSubtitleComponent />
                      </LandingHeadingWrapper>
                    );
                  case "component--landing-paragraph":
                    return (
                      <LandingParagraph
                        ref={paragraphRef}
                        key={`${component.key}-${index}`}
                        dangerouslySetInnerHTML={{
                          __html: component.content,
                        }}
                      />
                    );
                  default:
                    return null;
                }
              })}
            </ScrollingContainer>
          </LandingScrollingContainer>
          <LandingSubtitleComponent sceneRef={sceneRef} desktop={true} />
        </LandingScrollingWrapper>
      </MainLandingContainer>
    </Wrapper>
  );
};

export default LandingComponent;
